import type { i18n as I18n } from 'i18next';
import { translateTime } from 'flow/utils/browser-utils';

/**
 * Compact duration format using locale-specific short symbols.
 * Example output (English): "45 s", "3 min 20 s", "1 h 30 min".
 * Symbols come from the `duration.shortSymbol.*` translation keys.
 */
const formatDurationCompact = (totalSeconds: number, i18next: I18n) => {
    const secSymbol = i18next.t('duration.shortSymbol.sec');
    const minSymbol = i18next.t('duration.shortSymbol.min');
    const hourSymbol = i18next.t('duration.shortSymbol.hour');

    if (totalSeconds < 60) {
        return `${totalSeconds} ${secSymbol}`;
    }

    const totalMinutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (totalMinutes < 60) {
        const base = `${totalMinutes} ${minSymbol}`;
        return seconds > 0 ? `${base} ${seconds} ${secSymbol}` : base;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const base = `${hours} ${hourSymbol}`;
    return minutes > 0 ? `${base} ${minutes} ${minSymbol}` : base;
};

/**
 * Long duration format using pluralized unit words.
 * Example output (English): "45 seconds", "3 minutes 20 seconds", "1 hour 30 minutes".
 * Unit words come from the `datetime.{seconds,minutes,hours}` translation keys.
 */
const formatDurationLong = (totalSeconds: number, i18next: I18n) => {
    if (totalSeconds < 60) {
        return i18next.t('datetime.seconds', { count: totalSeconds });
    }

    const totalMinutes = Math.floor(totalSeconds / 60);

    if (totalMinutes < 60) {
        const seconds = totalSeconds % 60;
        const base = i18next.t('datetime.minutes', { count: totalMinutes });
        return seconds > 0
            ? `${base} ${i18next.t('datetime.seconds', { count: seconds })}`
            : base;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const base = i18next.t('datetime.hours', { count: hours });
    return minutes > 0
        ? `${base} ${i18next.t('datetime.minutes', { count: minutes })}`
        : base;
};

/**
 * Formats a duration given in milliseconds for display. Picks a compact or
 * long format, then runs the result through `translateTime` so Arabic digits
 * are transliterated. Returns `undefined` for nullish input.
 */
export const formatDurationMs = (
    ms: number | undefined,
    compact: boolean,
    i18next: I18n,
): string | undefined => {
    if (!ms) return undefined;
    const seconds = Math.floor(ms / 1000);
    const formatted = compact
        ? formatDurationCompact(seconds, i18next)
        : formatDurationLong(seconds, i18next);
    return translateTime(formatted);
};
